import React from 'react';
import { Calendar, DollarSign, CheckCircle, FileEdit as Edit } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Button from '../ui/Button';

interface ActiveContractCardProps {
  contract: {
    id: string;
    type: 'cold_calling' | 'mailing' | 'growth';
    billing_unit: 'days' | 'campaign' | 'package';
    start_date: string;
    end_date: string | null;
    days_per_month: number | null;
    monthly_price: number | null;
    price_per_day: number | null;
    notes: string | null;
  };
  onRenew: (contract: any) => void;
  onEdit: (contract: any) => void;
}

const ActiveContractCard: React.FC<ActiveContractCardProps> = ({
  contract,
  onRenew,
  onEdit,
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Non définie';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch {
      return 'Non définie';
    }
  };

  const getTypeBadge = () => {
    switch (contract.type) {
      case 'cold_calling':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
            📞 Cold Calling
          </span>
        );
      case 'mailing':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
            📧 Mailing
          </span>
        );
      case 'growth':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 text-sm font-medium rounded-full">
            🚀 Growth
          </span>
        );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 relative">
      <div className="flex items-start justify-between mb-4">
        {getTypeBadge()}
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
          <CheckCircle className="h-4 w-4" />
          Actif
        </span>
      </div>

      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="h-4 w-4" />
          <span>
            {formatDate(contract.start_date)} → {formatDate(contract.end_date)}
          </span>
        </div>

        {contract.type === 'cold_calling' && (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Jours/mois:</span>
              <span className="font-semibold text-gray-900">{contract.days_per_month} jours</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Prix mensuel:</span>
              <span className="font-semibold text-[#E91E8C]">
                {contract.monthly_price?.toLocaleString('fr-FR')} €/mois
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Prix/jour:</span>
              <span className="font-semibold text-gray-900">
                {contract.price_per_day?.toFixed(2)} €/jour
              </span>
            </div>
          </>
        )}

        {contract.type === 'mailing' && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Prix:</span>
            <span className="font-semibold text-[#E91E8C]">
              {contract.monthly_price?.toLocaleString('fr-FR')} €/mois
            </span>
          </div>
        )}

        {contract.type === 'growth' && (
          <>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Prix package:</span>
              <span className="font-semibold text-[#E91E8C]">
                {contract.monthly_price?.toLocaleString('fr-FR')} €
              </span>
            </div>
            {contract.notes && (
              <div className="text-sm">
                <span className="text-gray-600 block mb-1">Notes:</span>
                <p className="text-gray-700 bg-gray-50 p-2 rounded">{contract.notes}</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className="pt-4 border-t border-gray-200 flex gap-2">
        <Button
          variant="secondary"
          onClick={() => onEdit(contract)}
          className="flex-1 flex items-center justify-center gap-2"
        >
          <Edit className="h-4 w-4" />
          Modifier
        </Button>
        <Button
          variant="secondary"
          onClick={() => onRenew(contract)}
          className="flex-1"
        >
          Renouveler
        </Button>
      </div>
    </div>
  );
};

export default ActiveContractCard;
